"use client";

import {
  Archive,
  CircleDot,
  Compass,
  Frown,
  Sparkles,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

const CANCELLATION_REASONS = [
  {
    value: "too_expensive",
    labelKey: "cancellation.reason.tooExpensive" as const,
    icon: Wallet,
  },
  {
    value: "need_more_features",
    labelKey: "cancellation.reason.needMoreFeatures" as const,
    icon: Sparkles,
  },
  {
    value: "found_alternative",
    labelKey: "cancellation.reason.foundAlternative" as const,
    icon: Compass,
  },
  {
    value: "no_longer_need",
    labelKey: "cancellation.reason.noLongerNeed" as const,
    icon: Archive,
  },
  {
    value: "quality_less_than_expected",
    labelKey: "cancellation.reason.qualityLessThanExpected" as const,
    icon: Frown,
  },
  {
    value: "other",
    labelKey: "cancellation.reason.other" as const,
    icon: CircleDot,
  },
];

interface ReasonStepProps {
  selectedReason: string | null;
  onSelectReason: (reason: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

export const ReasonStep = ({
  selectedReason,
  onSelectReason,
  onBack,
  onContinue,
}: ReasonStepProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        {CANCELLATION_REASONS.map((reason) => (
          <button
            key={reason.value}
            type="button"
            onClick={() => onSelectReason(reason.value)}
            className={cn(
              "flex items-center gap-3 rounded-lg border p-3.5 text-left transition-colors cursor-pointer",
              selectedReason === reason.value
                ? "border-brand-primary bg-brand-primary/5"
                : "border-border hover:border-muted-foreground/30",
            )}
          >
            <reason.icon className="size-4 shrink-0 text-muted-foreground" />
            <span className="text-sm text-foreground">
              {t(reason.labelKey)}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} size="sm">
          {t("cancellation.reason.goBack")}
        </Button>
        <Button
          onClick={onContinue}
          disabled={!selectedReason}
          size="sm"
          className="border border-red-200 bg-white text-destructive shadow-sm hover:bg-slate-100"
        >
          {t("cancellation.reason.continueCanceling")}
        </Button>
      </div>
    </div>
  );
};
